import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ArrowLeft, Play, CheckCircle2, Circle, ChevronDown, ChevronUp,
  Lock, Menu, X, Award, Download
} from 'lucide-react';
import CourseReviewForm from './CourseReviewForm';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function formatDurationShort(seconds) {
  if (!seconds) return '';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) return `${hrs}h ${mins > 0 ? `${mins}m` : ''}`;
  if (mins > 0) return `${mins}m`;
  return `${seconds}s`;
}

const CoursePlayerPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentLesson, setCurrentLesson] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoLoading, setVideoLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [progress, setProgress] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [certEligible, setCertEligible] = useState(false);
  const [certDownloading, setCertDownloading] = useState(false);

  const token = localStorage.getItem('subscriber_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchCourse();
  }, [courseId, token]);

  const fetchCourse = async () => {
    try {
      const res = await fetch(`${API_URL}/api/courses/enrolled/${courseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401) {
        navigate('/login');
        return;
      }
      if (res.status === 403) {
        setError('You are not enrolled in this course or your access has expired.');
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error('Failed to load course');
      const data = await res.json();
      setCourse(data.course);
      setProgress(data.course?.enrollment?.progress || {});
      // Expand all sections
      const expanded = {};
      data.course?.sections?.forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
      // Auto-select first lesson
      if (data.course?.sections?.[0]?.lessons?.[0]) {
        selectLesson(data.course.sections[0].lessons[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectLesson = useCallback(async (lesson) => {
    if (!lesson.video_storage_path) {
      setCurrentLesson(lesson);
      setVideoUrl('');
      return;
    }
    setCurrentLesson(lesson);
    setVideoLoading(true);
    // Revoke old blob URL
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    try {
      const res = await fetch(`${API_URL}/api/courses/video/${lesson.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load video');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setVideoUrl(blobUrl);
    } catch (err) {
      console.error('Video load error:', err);
      setVideoUrl('');
    } finally {
      setVideoLoading(false);
    }
  }, [token, videoUrl]);

  const markComplete = async (lessonId) => {
    const completed = !progress[lessonId];
    setProgress(prev => ({ ...prev, [lessonId]: completed }));
    try {
      await fetch(`${API_URL}/api/courses/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ course_id: courseId, lesson_id: lessonId, completed })
      });
    } catch (err) {
      console.error('Progress update failed:', err);
    }
    // Re-check certificate eligibility
    checkCertificateEligibility();
  };

  const checkCertificateEligibility = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/courses/certificate/${courseId}/check`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCertEligible(data.eligible);
      }
    } catch (err) {
      console.error('Certificate check failed:', err);
    }
  }, [courseId, token]);

  // Check certificate eligibility when course loads
  useEffect(() => {
    if (course) checkCertificateEligibility();
  }, [course, checkCertificateEligibility]);

  const downloadCertificate = async () => {
    setCertDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/certificate/${courseId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Failed to generate certificate');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Certificate_${course?.title?.replace(/\s+/g, '_').slice(0, 30)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Certificate download failed:', err);
      alert('Failed to download certificate');
    } finally {
      setCertDownloading(false);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  // Prevent right-click on video
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  const totalLessons = course?.sections?.reduce((acc, s) => acc + (s.lessons?.length || 0), 0) || 0;
  const completedLessons = Object.values(progress).filter(Boolean).length;
  const progressPercent = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-golden border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-4">
        <Lock className="w-16 h-16 text-golden mb-4" />
        <h2 className="text-xl mb-2">{error}</h2>
        <Button onClick={() => navigate('/courses')} className="bg-golden text-black hover:bg-golden/90 mt-4">
          Browse Courses
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Top Bar */}
      <header className="bg-black/90 border-b border-golden/30 px-4 py-3 flex items-center justify-between flex-shrink-0 z-50">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-golden hover:text-golden-light" data-testid="back-dashboard-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-white font-medium truncate max-w-md" data-testid="player-course-title">{course?.title}</h1>
        </div>
        <div className="flex items-center gap-4">
          {certEligible && (
            <Button
              size="sm"
              onClick={downloadCertificate}
              disabled={certDownloading}
              className="bg-golden text-black hover:bg-golden/90 text-xs hidden sm:flex items-center gap-1"
              data-testid="download-certificate-btn"
            >
              <Award className="w-4 h-4" />
              {certDownloading ? 'Generating...' : 'Certificate'}
            </Button>
          )}
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-golden rounded-full transition-all" style={{ width: `${progressPercent}%` }}></div>
            </div>
            <span className="text-golden text-xs font-medium">{progressPercent}%</span>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden text-golden"
            data-testid="toggle-sidebar-btn"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Video Player Area */}
        <div className={`flex-1 flex flex-col ${sidebarOpen ? 'hidden lg:flex' : 'flex'}`}>
          <div className="flex-1 bg-gray-950 flex items-center justify-center relative" onContextMenu={handleContextMenu}>
            {videoLoading ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-2 border-golden border-t-transparent rounded-full animate-spin"></div>
                <span className="text-gray-400 text-sm">Loading video...</span>
              </div>
            ) : videoUrl ? (
              <div className="w-full h-full relative" onContextMenu={handleContextMenu}>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  className="w-full h-full object-contain"
                  onContextMenu={handleContextMenu}
                  data-testid="video-player"
                  onEnded={() => {
                    if (currentLesson) markComplete(currentLesson.id);
                  }}
                />
                {/* Overlay to prevent easy right-click */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: 'transparent' }}
                ></div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 text-gray-500">
                <Play className="w-16 h-16" />
                <span>{currentLesson ? 'No video uploaded for this lesson' : 'Select a lesson to start'}</span>
              </div>
            )}
          </div>

          {/* Current lesson info */}
          {currentLesson && (
            <div className="bg-black/80 border-t border-golden/20 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-white font-medium" data-testid="current-lesson-title">{currentLesson.title}</h3>
                {currentLesson.duration_seconds > 0 && (
                  <span className="text-gray-400 text-sm">{formatDurationShort(currentLesson.duration_seconds)}</span>
                )}
              </div>
              <Button
                variant={progress[currentLesson.id] ? "outline" : "default"}
                size="sm"
                onClick={() => markComplete(currentLesson.id)}
                className={progress[currentLesson.id]
                  ? "border-green-500 text-green-400 hover:bg-green-500/10"
                  : "bg-golden text-black hover:bg-golden/90"
                }
                data-testid="mark-complete-btn"
              >
                {progress[currentLesson.id] ? (
                  <><CheckCircle2 className="w-4 h-4 mr-1" /> Completed</>
                ) : (
                  'Mark Complete'
                )}
              </Button>
            </div>
          )}

          {/* Certificate Banner */}
          {certEligible && (
            <div className="bg-gradient-to-r from-golden/20 via-golden/10 to-golden/20 border-t border-golden/30 px-6 py-3 flex items-center justify-between" data-testid="certificate-banner">
              <div className="flex items-center gap-3">
                <Award className="w-6 h-6 text-golden" />
                <div>
                  <p className="text-golden font-semibold text-sm">Course Completed!</p>
                  <p className="text-gray-400 text-xs">You've completed all lessons. Download your certificate.</p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={downloadCertificate}
                disabled={certDownloading}
                className="bg-golden text-black hover:bg-golden/90 flex items-center gap-1"
                data-testid="banner-download-cert-btn"
              >
                <Download className="w-4 h-4" />
                {certDownloading ? 'Generating...' : 'Download Certificate'}
              </Button>
            </div>
          )}

          {/* Rate this course (for any enrolled subscriber, not gated by completion) */}
          {course && (
            <CourseReviewForm courseId={courseId} token={token} courseTitle={course.title} />
          )}
        </div>

        {/* Sidebar - Course Content */}
        <div
          className={`${sidebarOpen ? 'flex' : 'hidden lg:flex'} w-full lg:w-80 xl:w-96 flex-shrink-0 flex-col bg-gray-950 border-l border-golden/20 overflow-hidden`}
          data-testid="lesson-sidebar"
        >
          <div className="px-4 py-3 border-b border-golden/20 bg-black/50">
            <h3 className="text-golden font-semibold text-sm">Course Content</h3>
            <p className="text-gray-500 text-xs mt-1">{completedLessons}/{totalLessons} completed</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {course?.sections?.map((section, idx) => (
              <div key={section.id} className="border-b border-white/5">
                <button
                  onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
                >
                  <span className="text-white text-sm font-medium text-left truncate">
                    {idx + 1}. {section.title}
                  </span>
                  {expandedSections[section.id] ? (
                    <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedSections[section.id] && (
                  <div>
                    {section.lessons?.map(lesson => {
                      const isActive = currentLesson?.id === lesson.id;
                      const isCompleted = progress[lesson.id];
                      return (
                        <button
                          key={lesson.id}
                          onClick={() => { selectLesson(lesson); setSidebarOpen(false); }}
                          className={`w-full flex items-center gap-3 px-6 py-2.5 text-left transition-colors ${
                            isActive ? 'bg-golden/10 border-l-2 border-golden' : 'hover:bg-white/5'
                          }`}
                          data-testid={`lesson-${lesson.id}`}
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <Circle className="w-4 h-4 text-gray-600 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm block truncate ${isActive ? 'text-golden' : 'text-gray-300'}`}>
                              {lesson.title}
                            </span>
                          </div>
                          {lesson.duration_seconds > 0 && (
                            <span className="text-gray-500 text-xs flex-shrink-0">{formatDurationShort(lesson.duration_seconds)}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePlayerPage;
